import { Download, ExternalLink, Loader2 } from 'lucide-react';

import { FileViewerShell } from '@/components/shared/FileViewerShell';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/api/client';
import { usePdfObjectUrl } from '@/hooks/usePdfObjectUrl';
import { useToast } from '@/hooks/use-toast';
import { downloadResponseAsFile } from '@/lib/utils';

/**
 * Looking at a PDF this API serves, without saving a copy of it.
 *
 * One window for every PDF in the app: a generated invoice, and (NEW-238) the
 * archived copy of the one a client was emailed. The iframe renders the
 * browser's own viewer off a blob: URL — see usePdfObjectUrl for why the bytes
 * cannot simply be a src.
 */
export interface PdfViewerDialogProps {
  /** Open when non-null; null closes the dialog and fetches nothing. */
  path: string | null;
  /** Window title: the filename, which is also the viewer's accessible name. */
  title: string;
  /**
   * Saved filename. Needed even though the response names itself:
   * Content-Disposition is not in the API's CORS exposeHeaders, so in
   * production the browser cannot read it and downloadResponseAsFile falls
   * back to this.
   */
  downloadName: string;
  onClose: () => void;
}

export function PdfViewerDialog({ path, title, downloadName, onClose }: PdfViewerDialogProps) {
  const { toast } = useToast();
  const { url, isLoading, isError } = usePdfObjectUrl(path);

  // Asks the server again rather than saving the blob already on screen, so a
  // download is never a copy of a stale render.
  const handleDownload = async () => {
    if (!path) return;
    const progress = toast({ title: 'Downloading PDF…' });
    try {
      const response = await apiClient.get<Response>(path);
      await downloadResponseAsFile(response, downloadName);
    } catch {
      toast({
        title: 'Download failed',
        description: 'Could not download the PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      progress.dismiss();
    }
  };

  // iOS Safari renders only the first page of a PDF inside an iframe and will
  // not scroll it. Handing the same blob to a tab gets the native viewer, which
  // scrolls, zooms and shares. On desktop it is simply a bigger window.
  const handleOpenInNewTab = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <FileViewerShell
      open={path != null}
      onClose={onClose}
      title={title}
      bodyClassName="bg-gray-100"
      actions={
        path && (
          <>
            <Button variant="outline" size="sm" disabled={!url} onClick={handleOpenInNewTab} className="shrink-0">
              <ExternalLink className="h-4 w-4" />
              Open in new tab
            </Button>
            <Button variant="outline" size="sm" onClick={() => void handleDownload()} className="shrink-0">
              <Download className="h-4 w-4" />
              Download
            </Button>
          </>
        )
      }
    >
      {isLoading && (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}

      {/* What a failure means depends on the caller: for a generated invoice it
          is a render that failed or a session that expired; for a send's
          archived copy it is an R2 object that is missing or unreadable, since
          those bytes are fetched, never re-rendered. The user can act on none
          of them from inside the viewer, so the message names none of them.
          Download stays available: it asks the server again, which is the only
          retry worth offering — and the one that can actually succeed, for the
          cases where the first attempt failed in transit. */}
      {isError && (
        <div className="flex h-full items-center justify-center px-6 text-center">
          <p className="text-sm text-gray-600">
            Could not load this PDF. Try again, or download it instead.
          </p>
        </div>
      )}

      {url && <iframe src={url} title={title} className="h-full w-full border-0 bg-white" />}
    </FileViewerShell>
  );
}
